import { User } from "tree-wallet-client";
import { UserTypes } from "../Repository/UserRepository";

// FormData stringifies everything it is given, so appending undefined or null
// sends the literal text "undefined" / "null" for what the API expects to be a
// number. A missing optional value has to stay missing.
export function isEmptyValue(value: any) {
  return value === undefined
    || value === null
    || value === 'undefined'
    || value === 'null'
    || value === 'NaN'
    || (typeof value === 'number' && isNaN(value));
}

const MIME_EXTENSIONS: { [mimeType: string]: string } = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
};

/**
 * Name to upload a binary part under.
 *
 * A File keeps the name it was created with, but a plain Blob has none: FormData
 * then uploads it as "blob", with no extension at all, and the API answers with
 * a 500. Blobs reach us from `fetch(blobUrl).then(r => r.blob())` and from image
 * compression, so the name has to be rebuilt from the mime type.
 */
export function blobFilename(key: string, blob: Blob) {
  // "blob" is the name FormData itself gives to a nameless part, so trees
  // already queued with a broken photo name are renamed too.
  const name = (blob as File).name === 'blob' ? '' : (blob as File).name;

  if (name && name.lastIndexOf('.') > 0) {
    return name;
  }

  const rawType = (blob.type || 'image/jpeg').split(';')[0].toLowerCase();
  const subType = rawType.split('/')[1] || '';
  const extension = MIME_EXTENSIONS[rawType] || subType.replace(/[^a-z0-9]/gi, '') || 'jpg';

  return `${name || key}.${extension}`;
}

export function entriesToFormData(data: any) {
  const formData = new FormData();
  data.map((content: any) => {
    const [key, value] = content;

    if (key === 'altitude' && isEmptyValue(value)) {
      formData.append('altitude', '0');
      return;
    }
    if (isEmptyValue(value)) {
      return;
    }
    if (value instanceof Blob) {
      const filename = blobFilename(key, value);
      const rawType = (value.type || 'image/jpeg').split(';')[0].toLowerCase();
      const cleanMimeType = MIME_EXTENSIONS[rawType] ? rawType : 'image/jpeg';

      // In Safari (WebKit), Blobs deserialized from IndexedDB can fail to stream
      // in FormData/fetch (sending 0 bytes or broken headers, causing a server 500 error).
      // Re-wrapping the Blob into a fresh File/Blob instance ensures Safari streams it correctly.
      let safeFile: Blob;
      try {
        safeFile = new File([value], filename, { type: cleanMimeType });
      } catch (e) {
        safeFile = new Blob([value], { type: cleanMimeType });
      }

      formData.append(key, safeFile, filename);
      return;
    }
    formData.append(key, value);
  });
  return formData;
}

/**
 * Readable snapshot of a payload, safe to log: binary parts are reduced to their
 * name/type/size and long values are cut, so a rejected request can be traced
 * back to the offending field from the device itself (/test -> Errors).
 */
export function describeFormData(formData: FormData) {
  const described: any = {};

  formData.forEach((value: any, key: string) => {
    if (value instanceof Blob) {
      const filename = value instanceof File ? value.name : 'blob';
      described[key] = `${filename} (${value.type || 'unknown type'}, ${value.size} bytes)`;
      return;
    }
    const asString = String(value);
    described[key] = asString.length > 120 ? `${asString.substring(0, 120)}...` : asString;
  });

  return described;
}

export function getUserName(user: User) {
  if (UserTypes.Individual === user.type) {
    return `${user.firstName} ${user.lastName}`
  }
  return user.company;
}

export function processObject(data: any, treeData: any) {
  for (const idx in data) {
    const current = data[idx];
    try {
      if (!current) {
        treeData[idx] = current;
      }
      else if (typeof current === 'object') {
        const object = processObject(current, {});
        treeData[idx] = JSON.parse(JSON.stringify(object));
      }
      else if (typeof current === 'string') {
        treeData[idx] = JSON.parse(data[idx]);
      }
      else {
        treeData[idx] = data[idx];
      }
    } catch (err) {
      console.error(err);
    }
  }
  return treeData;
}

export function dataOrNull(data?: any) {
  return data !== undefined ? data : null;
}

export function stringOrNull(data?: any) {
  if (data === undefined || data === null) {
    return null;
  }
  return String(data)
}

export function floatOrNull(data?: any) {
  return data !== undefined ? parseFloat(String(data)) : null;
}

export function integerOrNull(data?: any) {
  return data !== undefined ? parseInt(String(data)) : null;
}

export function toObject(data?: any) {
  if (!data) {
    return data;
  }
  if (typeof data === 'string') {
    try {
      const transformed = JSON.parse(data);
      if (typeof transformed === 'object') {
        return transformed;
      }
      throw new Error('could not transform to object');
    } catch (err) {
      console.error('error while trying to convert to an object', { err, data })
      return data;
    }
  }
  return data;
}